import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { PublicSharePage } from "./share/PublicSharePage";

// Marketing landing for ultradrive.ai + the public-link viewer at
// `/s/:token`. Both routes live in this bundle because the Express
// SPA catchall at `apps/api/src/routes/spa.ts` already serves landing's
// build for any path that isn't `/v1`/`/api`/`/admin/api`/`/health`/
// `/readyz`. The dashboard SPA can't host /s/* because its router is
// pinned to `basename="/app"` (Auth0 callback registration).

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MarketingApp />} />
        <Route path="/s/:token" element={<PublicSharePage />} />
        {/* Anything else falls back to marketing — same behaviour as
            the SPA catchall, just keeps the URL bar honest. */}
        <Route path="*" element={<MarketingApp />} />
      </Routes>
    </BrowserRouter>
  );
}

// Marketing landing — glass-card + light/dark, decorative gradient
// backdrop. Pricing numbers are placeholders — set ?showPricing=false
// in the router to hide if we want to ship without commitment.
function MarketingApp() {
  return (
    <div className="relative overflow-x-clip">
      <Backdrop />
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ─── Decorative backdrop ────────────────────────────────────────────

function Backdrop() {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
      <div
        className="blob bg-sky-300 dark:bg-sky-600"
        style={{ width: 600, height: 600, top: -120, left: -120 }}
      />
      <div
        className="blob bg-violet-300 dark:bg-violet-600"
        style={{ width: 700, height: 700, top: 800, right: -200 }}
      />
      <div
        className="blob bg-emerald-200 dark:bg-emerald-700"
        style={{ width: 500, height: 500, top: 1800, left: "30%" }}
      />
      {/* Subtle grid pattern over the blobs adds depth without being noisy. */}
      <div
        className="absolute inset-0 opacity-[0.06] dark:opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          color: "currentColor",
        }}
      />
    </div>
  );
}

// ─── Navigation ─────────────────────────────────────────────────────

function Nav() {
  return (
    <header className="sticky top-0 z-20 border-b border-zinc-200/40 dark:border-white/5 bg-white/60 dark:bg-slate-950/60 backdrop-blur-xl">
      <div className="mx-auto max-w-6xl px-6 h-14 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 font-bold tracking-tight">
          <Logo />
          <span>UltraDrive</span>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-700 dark:text-slate-300">
          <a href="#features" className="hover:text-zinc-900 dark:hover:text-white">
            Features
          </a>
          <a href="#how" className="hover:text-zinc-900 dark:hover:text-white">
            How it works
          </a>
          <a href="#pricing" className="hover:text-zinc-900 dark:hover:text-white">
            Pricing
          </a>
        </nav>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="/app"
            className="rounded-full bg-zinc-900 dark:bg-white px-4 py-1.5 text-sm font-medium text-white dark:text-zinc-900 hover:bg-black dark:hover:bg-zinc-100 transition"
          >
            Open Dashboard
          </a>
        </div>
      </div>
    </header>
  );
}

function Logo() {
  return (
    <img
      src={`${import.meta.env.BASE_URL}logo.png`}
      alt="UltraDrive"
      className="w-6 h-6"
    />
  );
}

// ─── Hero ───────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 pt-20 pb-24 text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/60 backdrop-blur px-3 py-1 text-xs text-zinc-700 dark:text-slate-300 mb-8">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Now with semantic search across every photo, video, and RAW
      </div>
      <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05]">
        Your media library,
        <br />
        <span className="bg-gradient-to-r from-sky-500 via-violet-500 to-pink-500 bg-clip-text text-transparent">
          actually searchable.
        </span>
      </h1>
      <p className="mt-6 text-lg md:text-xl text-zinc-600 dark:text-slate-400 max-w-2xl mx-auto">
        UltraDrive watches your folders, ingests every shot, reads the
        EXIF, runs vision models, and lets you find <em>"red sportbike at
        the Alps"</em> across years of footage. Self-hosted, sync-friendly,
        works offline.
      </p>
      <div className="mt-10 flex items-center justify-center gap-3">
        <a
          href="/app"
          className="rounded-full bg-zinc-900 dark:bg-white px-6 py-3 text-base font-medium text-white dark:text-zinc-900 hover:bg-black dark:hover:bg-zinc-100 transition"
        >
          Start free
        </a>
        <a
          href="#how"
          className="rounded-full border border-zinc-300 dark:border-white/20 px-6 py-3 text-base font-medium text-zinc-800 dark:text-slate-100 hover:bg-zinc-100/60 dark:hover:bg-white/5 transition"
        >
          See how it works
        </a>
      </div>
      <p className="mt-4 text-xs text-zinc-500 dark:text-slate-500">
        No credit card · Mac sync app included
      </p>

      {/* Hero visual — simulated dashboard tile. */}
      <div className="mt-16 mx-auto max-w-4xl glass rounded-2xl p-2">
        <div className="rounded-xl bg-white dark:bg-slate-950 p-6 text-left">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-slate-700" />
            <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-slate-700" />
            <div className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-slate-700" />
            <div className="ml-4 px-3 py-1 rounded-md bg-zinc-100 dark:bg-slate-900 text-xs text-zinc-500 dark:text-slate-400 font-mono">
              search: "ducati at the track"
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {["bg-sky-200 dark:bg-sky-900", "bg-violet-200 dark:bg-violet-900", "bg-emerald-200 dark:bg-emerald-900", "bg-amber-200 dark:bg-amber-900", "bg-rose-200 dark:bg-rose-900", "bg-cyan-200 dark:bg-cyan-900", "bg-indigo-200 dark:bg-indigo-900", "bg-lime-200 dark:bg-lime-900"].map((c, i) => (
              <div
                key={i}
                className={`aspect-[4/3] rounded ${c} flex items-end p-2 text-[10px] font-mono text-zinc-700 dark:text-slate-300`}
              >
                DSC0{787 + i}.ARW
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-zinc-500 dark:text-slate-500">
            8 of 1,247 results · Sony A7M4 · Alps · 2025
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Features ───────────────────────────────────────────────────────

function Features() {
  return (
    <section id="features" className="relative mx-auto max-w-6xl px-6 py-24">
      <SectionHeading
        eyebrow="Features"
        title="Built for creators who shoot a lot"
        subtitle="Every shot indexed. Every RAW previewable in browser. Every search across years of footage."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </div>
    </section>
  );
}

const FEATURES: Array<{ title: string; body: string; icon: React.ReactElement }> = [
  {
    title: "Camera RAW preview",
    body:
      "Sony ARW, Canon CR2/3, Nikon NEF, Fuji RAF, DNG and friends — rendered to JPEG proxies in the background. The originals stay byte-identical.",
    icon: <IconCamera />,
  },
  {
    title: "Semantic search",
    body:
      "Find photos by what's in them. \"red sportbike\" surfaces Ducati shots even if neither the filename nor your tags say so.",
    icon: <IconSearch />,
  },
  {
    title: "Mac sync agent",
    body:
      "Drop files into a watched folder; UltraDrive uploads them resumably (tus.io) with progress bars in the menubar.",
    icon: <IconSync />,
  },
  {
    title: "EXIF + IPTC + XMP",
    body:
      "Every camera, every lens, every keyword you set in Lightroom. Searchable. Editable. Never lost.",
    icon: <IconTag />,
  },
  {
    title: "AI tagging",
    body:
      "Vision models run on every image: description, objects, scene, dominant colors. Pick your model per-endpoint.",
    icon: <IconSparkle />,
  },
  {
    title: "Self-hostable",
    body:
      "Helm chart + GitOps. Or use our hosted version. Either way you own the data and the storage bucket.",
    icon: <IconServer />,
  },
];

function FeatureCard({
  title,
  body,
  icon,
}: {
  title: string;
  body: string;
  icon: React.ReactElement;
}) {
  return (
    <div className="glass rounded-2xl p-6 hover:translate-y-[-2px] transition-transform">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500/20 to-violet-500/20 dark:from-sky-400/30 dark:to-violet-400/30 text-sky-700 dark:text-sky-300 grid place-items-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-sm text-zinc-600 dark:text-slate-400">{body}</p>
    </div>
  );
}

// ─── How it works ───────────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      title: "Drop a folder",
      body:
        "Install the Mac agent (or just upload through the dashboard). Pick the folders you want watched.",
    },
    {
      title: "We index everything",
      body:
        "EXIF, IPTC, XMP, dominant colors, AI description, vector embeddings. RAW gets a JPEG preview. All in the background.",
    },
    {
      title: "Search like a human",
      body:
        "Filename, camera body, lens, keywords, AI tags, or natural language — all in one search box.",
    },
  ];
  return (
    <section id="how" className="relative mx-auto max-w-6xl px-6 py-24">
      <SectionHeading
        eyebrow="How it works"
        title="Three steps. No spreadsheets."
      />
      <ol className="mt-12 grid gap-6 md:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.title} className="glass rounded-2xl p-6">
            <div className="text-sm font-mono text-zinc-500 dark:text-slate-500 mb-2">
              0{i + 1}
            </div>
            <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
            <p className="text-sm text-zinc-600 dark:text-slate-400">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ─── Pricing ────────────────────────────────────────────────────────

function Pricing() {
  return (
    <section id="pricing" className="relative mx-auto max-w-6xl px-6 py-24">
      <SectionHeading
        eyebrow="Pricing"
        title="Start free. Pay when you outgrow the box."
        subtitle="Self-host for free. Hosted plans cover storage, AI inference, and backups. No seat fees."
      />
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        <PricingCard
          name="Free"
          price="€0"
          period="forever"
          description="Self-hosted. Full feature set."
          features={[
            "Unlimited workspaces",
            "All sync clients",
            "Bring your own R2 / S3",
            "Bring your own AI keys",
            "Community support",
          ]}
          cta="Self-host docs"
          href="https://github.com/Uszi/ultra-infra"
        />
        <PricingCard
          name="Creator"
          price="€19"
          period="/month"
          description="Hosted, 250 GB storage, AI tagging on us."
          features={[
            "250 GB storage included",
            "Vision tagging up to 5k images / mo",
            "Mac sync agent + iOS",
            "Nightly backups",
            "Priority support",
          ]}
          cta="Start free trial"
          href="/app"
          highlighted
        />
        <PricingCard
          name="Studio"
          price="€79"
          period="/month"
          description="For teams. 2 TB, multi-seat, audit log."
          features={[
            "2 TB storage included",
            "Vision tagging up to 50k images / mo",
            "Up to 10 editors",
            "SSO + audit log",
            "Dedicated channel",
          ]}
          cta="Talk to sales"
          href="mailto:hi@ultradrive.ai"
        />
      </div>
      <p className="mt-8 text-center text-xs text-zinc-500 dark:text-slate-500">
        Beta pricing — locked for early adopters once we leave beta.
      </p>
    </section>
  );
}

function PricingCard({
  name,
  price,
  period,
  description,
  features,
  cta,
  href,
  highlighted,
}: {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  href: string;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`glass rounded-2xl p-6 flex flex-col ${
        highlighted ? "ring-2 ring-sky-500/50" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold">{name}</h3>
        {highlighted && (
          <span className="text-[10px] uppercase tracking-wide rounded bg-sky-500/20 text-sky-700 dark:text-sky-300 px-2 py-0.5">
            Popular
          </span>
        )}
      </div>
      <div className="mt-1">
        <span className="text-4xl font-bold">{price}</span>
        <span className="text-sm text-zinc-500 dark:text-slate-400 ml-1">{period}</span>
      </div>
      <p className="mt-2 text-sm text-zinc-600 dark:text-slate-400">{description}</p>
      <ul className="mt-6 space-y-2 text-sm flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <svg
              viewBox="0 0 20 20"
              className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z"
              />
            </svg>
            <span className="text-zinc-700 dark:text-slate-300">{f}</span>
          </li>
        ))}
      </ul>
      <a
        href={href}
        className={`mt-6 block text-center rounded-full px-4 py-2.5 text-sm font-medium transition ${
          highlighted
            ? "bg-zinc-900 text-white hover:bg-black dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            : "border border-zinc-300 dark:border-white/20 text-zinc-800 dark:text-slate-100 hover:bg-zinc-100/60 dark:hover:bg-white/5"
        }`}
      >
        {cta}
      </a>
    </div>
  );
}

// ─── Final CTA ──────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section className="relative mx-auto max-w-3xl px-6 py-24 text-center">
      <div className="glass rounded-3xl p-12">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
          Stop scrolling. Start finding.
        </h2>
        <p className="mt-3 text-zinc-600 dark:text-slate-400">
          Bring your years of camera output into one searchable place.
        </p>
        <a
          href="/app"
          className="mt-6 inline-block rounded-full bg-zinc-900 dark:bg-white px-6 py-3 text-base font-medium text-white dark:text-zinc-900 hover:bg-black dark:hover:bg-zinc-100 transition"
        >
          Open Dashboard →
        </a>
      </div>
    </section>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="relative border-t border-zinc-200/60 dark:border-white/5 mt-12">
      <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-zinc-500 dark:text-slate-500">
        <div className="flex items-center gap-2">
          <Logo />
          <span>© {new Date().getFullYear()} UltraDrive</span>
        </div>
        <div className="flex items-center gap-6">
          <a href="https://github.com/Uszi/UltraDrive" className="hover:text-zinc-700 dark:hover:text-slate-300">
            GitHub
          </a>
          <a href="/app" className="hover:text-zinc-700 dark:hover:text-slate-300">
            Dashboard
          </a>
          <a href="mailto:hi@ultradrive.ai" className="hover:text-zinc-700 dark:hover:text-slate-300">
            Contact
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Section heading ────────────────────────────────────────────────

function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="text-center">
      <div className="text-xs font-mono uppercase tracking-widest text-sky-600 dark:text-sky-400 mb-3">
        {eyebrow}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{title}</h2>
      {subtitle && (
        <p className="mt-3 text-zinc-600 dark:text-slate-400 max-w-2xl mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── Inline SVG icons (no dep) ─────────────────────────────────────

function IconCamera() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <path d="M3 8a2 2 0 012-2h2.5l1.5-2h6l1.5 2H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" strokeLinejoin="round" />
      <circle cx={12} cy={13} r={3.5} />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <circle cx={11} cy={11} r={6} />
      <path d="M21 21l-5.5-5.5" strokeLinecap="round" />
    </svg>
  );
}
function IconSync() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <path d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3M16 4v4h-4M8 20v-4h4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconTag() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <path d="M3 12V4h8l10 10-8 8-10-10z" strokeLinejoin="round" />
      <circle cx={7.5} cy={7.5} r={1.5} fill="currentColor" />
    </svg>
  );
}
function IconSparkle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" strokeLinecap="round" />
    </svg>
  );
}
function IconServer() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
      <rect x={3} y={4} width={18} height={6} rx={1.5} />
      <rect x={3} y={14} width={18} height={6} rx={1.5} />
      <circle cx={7} cy={7} r={1} fill="currentColor" />
      <circle cx={7} cy={17} r={1} fill="currentColor" />
    </svg>
  );
}
